import React, { useState } from 'react';
import {
    Button,
    Text,
    makeStyles,
    Spinner,
    Input,
    Label,
    Switch,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionPanel,
    Dropdown,
    Option,
    Slider,
} from '@fluentui/react-components';
import { PeopleRegular, SettingsRegular } from '@fluentui/react-icons';
import Editor from 'react-simple-code-editor';
import { Highlight, themes } from 'prism-react-renderer';
import { useTokenService } from '../../tokenService';
import { CopilotService } from '../../Service/ServerAPI';
import { useSelector } from 'react-redux';
import { RootState } from '../../utils/store';
import JsonViewer from './JsonViewer';
import {
    QualifiedSampleUsersResponse,
    QualifiedSampleUsersQueryParams
} from '../../types/DebugTypes';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        height: '100%',
    },
    editorSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    editorContainer: {
        backgroundColor: '#3A3E45',
        borderRadius: '4px',
        border: '1px solid #6B7584',
        maxHeight: '400px',
        overflow: 'auto',
    },
    editor: {
        fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
        fontSize: '13px',
        minHeight: '200px',
        padding: '12px',
    },
    configSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    configRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
    },
    publisherRoleContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    maxResultsContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    sliderRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
    },
    slider: {
        flex: 1,
    },
    sliderInput: {
        width: '60px',
    },
    buttonContainer: {
        display: 'flex',
        gap: '8px',
    },
    sampleUsersButton: {
        backgroundColor: '#107C10',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#0B5A08',
        },
    },
    buttonDisabled: {
        position: 'relative',
    },
    spinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    advancedOptions: {
        marginTop: '8px',
    },
    optionRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '12px',
    },
    optionLabel: {
        display: 'block',
        marginBottom: '4px',
    },
    responseSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
});

const DEFAULT_SCRIPT = `-- Enter your Lua script here
-- Example:
local function isUserIncluded(time, user_id, context)
    return true
end`;

const DebugSampleUsers: React.FC<{ dataset?: string }> = ({ dataset }) => {
    const classes = useStyles();
    const [script, setScript] = useState<string>(DEFAULT_SCRIPT);
    const [isGettingSamples, setIsGettingSamples] = useState(false);
    const [response, setResponse] = useState<QualifiedSampleUsersResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Sample Users configuration
    const [maxResults, setMaxResults] = useState<number>(5);
    const [publisherRole, setPublisherRole] = useState<'Xbox' | 'Activision'>('Xbox');
    const [scrollSize, setScrollSize] = useState<number>(10000);
    const [includeNestedData, setIncludeNestedData] = useState<boolean>(true);
    const [maxNestedEntries, setMaxNestedEntries] = useState<number>(100);
    const [dateOverride, setDateOverride] = useState<string>('');

    const getAccessToken = useTokenService();
    const copilotService = CopilotService.getInstance(getAccessToken);
    const settings = useSelector((state: RootState) => state.settings);

    const getSampleUsers = async () => {
        setIsGettingSamples(true);
        setError(null);

        try {
            const queryParams: QualifiedSampleUsersQueryParams = {
                publisherRole,
                dateOverride: dateOverride || undefined,
            };

            const result = await copilotService.getQualifiedSampleUsers(
                script,
                maxResults,
                scrollSize,
                undefined, // includeFields - optional, could add UI for this later
                includeNestedData,
                maxNestedEntries,
                queryParams,
                settings.sessionId,
                settings.apiEndpoint,
                dataset
            );

            if (result.error) {
                setError(result.error);
                setResponse(null);
            } else {
                // Filter out the qualificationScript from the response
                const { qualificationScript, ...filteredResult } = result;
                setResponse(filteredResult);
                setError(null);
            }
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'An unexpected error occurred';
            setError(errorMessage);
            setResponse(null);
        } finally {
            setIsGettingSamples(false);
        }
    };

    const LuaHighlighter = () => (
        <Highlight theme={themes.vsDark} code={script} language="lua">
            {({ tokens, getLineProps, getTokenProps }) => (
                <>
                    {tokens.map((line, i) => (
                        <div key={i} {...getLineProps({ line })}>
                            {line.map((token, key) => (
                                <span key={key} {...getTokenProps({ token })} />
                            ))}
                        </div>
                    ))}
                </>
            )}
        </Highlight>
    );

    return (
        <div className={classes.container}>
            <div className={classes.editorSection}>
                <Text weight="semibold">Lua Qualification Script</Text>
                <div className={classes.editorContainer}>
                    <Editor
                        value={script}
                        onValueChange={setScript}
                        highlight={LuaHighlighter}
                        padding={12}
                        disabled={isGettingSamples}
                        className={classes.editor}
                        style={{
                            fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
                            fontSize: '13px',
                        }}
                    />
                </div>
            </div>

            <div className={classes.configSection}>
                <div className={classes.configRow}>
                    <div className={classes.publisherRoleContainer}>
                        <Label htmlFor="publisherRole">Publisher Role</Label>
                        <Dropdown
                            id="publisherRole"
                            value={publisherRole}
                            selectedOptions={[publisherRole]}
                            onOptionSelect={(e, data) => setPublisherRole(data.optionValue as 'Xbox' | 'Activision')}
                            disabled={isGettingSamples}
                        >
                            <Option value="Xbox">Xbox</Option>
                            <Option value="Activision">Activision</Option>
                        </Dropdown>
                    </div>

                    <div className={classes.maxResultsContainer}>
                        <Label htmlFor="maxResults">Max Results (1-100)</Label>
                        <div className={classes.sliderRow}>
                            <Slider
                                className={classes.slider}
                                min={1}
                                max={100}
                                defaultValue={maxResults}
                                onChange={(e, data) => setMaxResults(data.value)}
                                disabled={isGettingSamples}
                            />
                            <Input
                                className={classes.sliderInput}
                                type="number"
                                value={maxResults.toString()}
                                onChange={(e, data) => {
                                    const val = parseInt(data.value) || 5;
                                    setMaxResults(Math.min(100, Math.max(1, val)));
                                }}
                                disabled={isGettingSamples}
                            />
                        </div>
                    </div>
                </div>

                <Accordion className={classes.advancedOptions} collapsible>
                    <AccordionItem value="advanced">
                        <AccordionHeader icon={<SettingsRegular />}>
                            Advanced Options
                        </AccordionHeader>
                        <AccordionPanel>
                            <div className={classes.optionRow}>
                                <Label htmlFor="scrollSize" className={classes.optionLabel}>
                                    Scroll Size (batch processing size)
                                </Label>
                                <Input
                                    id="scrollSize"
                                    type="number"
                                    value={scrollSize.toString()}
                                    onChange={(e, data) => setScrollSize(parseInt(data.value) || 10000)}
                                    disabled={isGettingSamples}
                                />
                            </div>

                            <div className={classes.optionRow}>
                                <Label htmlFor="maxNestedEntries" className={classes.optionLabel}>
                                    Max Nested Entries
                                </Label>
                                <Input
                                    id="maxNestedEntries"
                                    type="number"
                                    value={maxNestedEntries.toString()}
                                    onChange={(e, data) => setMaxNestedEntries(parseInt(data.value) || 100)}
                                    disabled={isGettingSamples}
                                />
                            </div>

                            <div className={classes.optionRow}>
                                <Switch
                                    checked={includeNestedData}
                                    onChange={(e, data) => setIncludeNestedData(data.checked)}
                                    label="Include Nested Data"
                                    disabled={isGettingSamples}
                                />
                            </div>

                            <div className={classes.optionRow}>
                                <Label htmlFor="dateOverride" className={classes.optionLabel}>
                                    Date Override (optional)
                                </Label>
                                <Input
                                    id="dateOverride"
                                    type="text"
                                    value={dateOverride}
                                    onChange={(e, data) => setDateOverride(data.value)}
                                    placeholder="YYYY-MM-DD"
                                    disabled={isGettingSamples}
                                />
                            </div>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </div>

            <div className={classes.buttonContainer}>
                <Button
                    appearance="primary"
                    icon={isGettingSamples ? undefined : <PeopleRegular />}
                    onClick={getSampleUsers}
                    disabled={isGettingSamples || !script.trim()}
                    className={
                        isGettingSamples
                            ? classes.buttonDisabled
                            : classes.sampleUsersButton
                    }
                >
                    {isGettingSamples ? (
                        <div className={classes.spinnerContainer}>
                            <Spinner size="tiny" />
                            <span>Getting Samples...</span>
                        </div>
                    ) : (
                        'Get Sample Users'
                    )}
                </Button>
            </div>

            <div className={classes.responseSection}>
                <JsonViewer data={response} error={error} />
            </div>
        </div>
    );
};

export default DebugSampleUsers;